import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Crown, Lock, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import OnlineDot from '../components/OnlineDot';
import { getLikesReceived, unlockUserPhotos } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { getPrimaryPhoto, toBlurredUrl } from '../services/photoHelpers';

const WhoLikedMePage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const defaultAvatar = useDefaultAvatar();
  const [data, setData] = useState({ results: [], total: 0, blur_enabled: false, unlock_credit_cost: 1, viewer_is_premium: false });
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getLikesReceived({ limit: 60 });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUnlock = async (targetId) => {
    setUnlocking(targetId);
    try {
      await unlockUserPhotos(targetId);
      // Refresh view + user balances
      await load();
      await checkAuth();
    } catch (e) {
      if (e.response?.status === 402) {
        if (window.confirm('You need more credits to unlock. Buy credits now?')) {
          navigate('/pricing');
        }
      } else {
        alert(e.response?.data?.detail || 'Unlock failed');
      }
    } finally {
      setUnlocking(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="who-liked-me-page">
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-testid="back">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Who Liked You</h1>
            <p className="text-xs text-gray-500">{data.total.toLocaleString()} {data.total === 1 ? 'like' : 'likes'}</p>
          </div>
          {!data.viewer_is_premium && data.blur_enabled && (
            <Button size="sm" className="bg-[#FFCE00] hover:bg-yellow-500 text-gray-900 rounded-full" onClick={() => navigate('/pricing')} data-testid="upgrade-cta">
              <Crown className="w-4 h-4 mr-1" /> Go Premium
            </Button>
          )}
        </div>
      </div>

      <div className="pt-20 px-4 max-w-3xl mx-auto">
        {loading && data.results.length === 0 ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent"></div>
          </div>
        ) : data.results.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center">
            <Heart className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">No likes yet</h3>
            <p className="text-sm text-gray-500">Keep swiping — someone will like you back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.results.map(({ user: u, liked_at, is_locked, action }) => {
              const rawPhoto = getPrimaryPhoto(u, defaultAvatar);
              const photo = is_locked ? toBlurredUrl(rawPhoto) : rawPhoto;
              return (
                <div key={u.user_id + liked_at} className="bg-white rounded-2xl overflow-hidden shadow-sm relative" data-testid={`like-${u.user_id}`}>
                  <div className="aspect-[3/4] bg-gray-100 relative">
                    <img src={photo || defaultAvatar} alt={u.name} className="w-full h-full object-cover" loading="lazy" />
                    {is_locked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/25">
                        <Lock className="w-8 h-8 text-white mb-2" />
                        <Button
                          size="sm"
                          className="bg-white text-gray-900 hover:bg-gray-100 rounded-full"
                          disabled={unlocking === u.user_id}
                          onClick={() => handleUnlock(u.user_id)}
                          data-testid={`unlock-${u.user_id}`}
                        >
                          {unlocking === u.user_id ? 'Unlocking…' : `Unlock (${data.unlock_credit_cost})`}
                        </Button>
                      </div>
                    )}
                    {action === 'superlike' && (
                      <span className="absolute top-2 right-2 bg-[#FFCE00] text-gray-900 text-[10px] font-medium px-2 py-0.5 rounded-full">Super like</span>
                    )}
                    {u.is_online && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-white" /> Online
                      </span>
                    )}
                  </div>
                  <div className="p-2.5">
                    <div className={`text-sm font-semibold ${is_locked ? 'text-gray-400' : 'text-gray-900'} truncate`}>
                      {is_locked ? 'Someone likes you' : `${u.display_name || u.name}${u.age ? `, ${u.age}` : ''}`}
                    </div>
                    {!is_locked && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 truncate">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{u.current_city || u.location || '—'}</span>
                      </div>
                    )}
                    {!u.is_online && (
                      <OnlineDot isOnline={false} lastActive={u.last_active} size={6} className="mt-1" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!data.viewer_is_premium && data.blur_enabled && data.results.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-100 rounded-xl text-sm text-yellow-900">
            <strong>Free member?</strong> Unlock any photo for {data.unlock_credit_cost} credit{data.unlock_credit_cost === 1 ? '' : 's'}, or{' '}
            <button onClick={() => navigate('/pricing')} className="underline font-medium">go premium</button> to reveal them all.
            {typeof user?.credits === 'number' && (
              <div className="text-xs text-yellow-800 mt-1">
                You have {(user.credits || 0) + (user.promo_credits || 0)} credit{((user.credits || 0) + (user.promo_credits || 0)) === 1 ? '' : 's'}
                {(user.promo_credits || 0) > 0 && <span> ({user.promo_credits} bonus)</span>}.
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default WhoLikedMePage;
